import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaArrowLeft, FaSearch, FaPlus, FaTrash, FaSave } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

const API = "https://nissi-india.com/api/storeIssueNote";

// ─── helpers ──────────────────────────────────────────────────────────────────
const todayStr = () => new Date().toISOString().split("T")[0];

// normalize every search row to ALWAYS have itemcode
const normalizeSearchItems = (rawItems = []) => {
  return rawItems
    .map((r) => {
      const display =
        typeof r === "string" ? r : r?.display || r?.itemname_itemcode || "";

      const itemcode =
        (typeof r === "object" && r?.itemcode) ||
        (display ? display.split("-")[0].trim() : "");

      return {
        display,
        itemcode,
        itemid: typeof r === "object" ? (r?.itemid ?? null) : null,
      };
    })
    .filter((x) => x.itemcode);
};

// map ANY SP row shape → your gridItems shape
const mapDbRowToGridRow = (r) => ({
  fld_itemid: r.fld_itemid ?? null,
  fld_itemcode: r.fld_itemcode ?? r.itemcode ?? "",
  fld_itemname: r.fld_itemname ?? r.itemname ?? "",

  availableQty: Number(
    r.fld_availableqty ??
      r.fld_availableQty ??
      r.fld_closing_stock ??
      r.fld_closingstock ??
      0
  ),
  requestQty: Number(r.fld_requestqty ?? r.fld_requestedqty ?? 0),

  issueQty: Number(
    r.fld_issueqty ??
      r.IssuedQty ??
      r.fld_qty ??
      r.fld_Qty ??
      r.fld_qtyissued ??
      0
  ),

  departmentCode: r.fld_DepartmentCode ?? r.fld_departmentcode ?? "",
  departmentName: r.fld_DepartmentName ?? r.fld_departmentname ?? "",

  vehicleCode: r.fld_VehicleCode ?? r.fld_vehichlecode ?? "",
  vehicleName: r.fld_VehicleName ?? r.fld_vehichlename ?? "",

  supplierCode: r.fld_suppliercode ?? "",
  supplierName: r.fld_suppliername ?? "",

  hsdfillinghrkm: String(r.fld_hsdfillinghrkm ?? "0"),
  hsdfillinghrkm1: String(
    r.fld_hsdfillingstartinghrkm ?? r.prevHsdHrKm ?? "0"
  ),
  isFuelCategory: !!r.isFuelCategory,

  detailId: r.fld_storesissuedetid ? Number(r.fld_storesissuedetid) : null,
});

const StoreIssueNoteAdd = () => {
  const navigate = useNavigate();
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const btn = `bg-${baseColor}-${baseShade} hover:bg-${baseColor}-700 text-white px-4 py-2 rounded-lg text-sm transition-colors`;

  // localStorage
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = parseInt(localStorage.getItem("branchId") || "0", 10);
  const branch = localStorage.getItem("branchType") || "";
  const loginUserId = parseInt(localStorage.getItem("userId") || "0", 10);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branch, loginUserId]
  );

  // session stored by listing handleEdit()
  const existingStoreIssueNo = sessionStorage.getItem("storeid") || null;
  const existingUserId = sessionStorage.getItem("storeuserid") || null;
  const existingIssueId = sessionStorage.getItem("storeissueid") || null;

  const isEditMode = !!existingIssueId;

  // header state
  const [storeIssueNo, setStoreIssueNo] = useState("");
  const [issueDate, setIssueDate] = useState(todayStr());
  const [currentStore, setCurrentStore] = useState("");
  const [storeList, setStoreList] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [approve] = useState(true);

  // radios
  const [issueType, setIssueType] = useState("FS");
  const [issueFor, setIssueFor] = useState("D");
  const [vehicleType, setVehicleType] = useState("O");

  // dropdowns
  const [itemCategories, setItemCategories] = useState([]);
  const [storeReqGrid, setStoreReqGrid] = useState([]);
  const [projectList] = useState([]);
  const [productionList, setProductionList] = useState([]);

  // filters
  const [itemCategory, setItemCategory] = useState("");
  const [itemName, setItemName] = useState("");
  const [itemCode, setItemCode] = useState("");
  const [projectId, setProjectId] = useState("");
  const [productionId, setProductionId] = useState("");

  // search list
  const [searchResults, setSearchResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // grid
  const [gridItems, setGridItems] = useState([]);

  // flags
  const [saving, setSaving] = useState(false);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    loadItemCategories();
    loadStores();
  }, []);

  useEffect(() => {
    if (storeList.length > 0 && currentStore === "") {
      setCurrentStore(
        String(storeList.find((s) => s.fld_storeid)?.fld_storeid || "")
      );
    }
  }, [storeList, currentStore]);

  useEffect(() => {
    if (isEditMode) {
      loadExistingDataFromSession();
    } else {
      generateIssueNo();
    }
  }, []);

  useEffect(() => {
    if (issueType === "FR") loadStoreReqGrid();
  }, [issueType]);

  useEffect(() => {
    if (projectId && projectId !== "Select") loadProductions(projectId);
  }, [projectId]);

  const loadItemCategories = () => {
    axios
      .get(`${API}/storeIssue_itemcategories`, {
        headers: commonHeaders,
      })
      .then((r) => setItemCategories(r.data.categories || []))
      .catch(() => toast.error("Failed to load item categories"));
  };

  const loadStores = () => {
    if (!branchId) {
      toast.error("Branch ID not found in localStorage");
      return;
    }

    axios
      .get(`${API}/storeIssue_storesFetch`, {
        params: { branchid: branchId },
        headers: commonHeaders,
      })
      .then((r) => setStoreList(r.data.stores || []))
      .catch(() => toast.error("Failed to load stores"));
  };

  const generateIssueNo = () => {
    if (!loginUserId) {
      toast.error("User ID not found in localStorage");
      return;
    }

    axios
      .post(
        `${API}/storeIssue_generateNo`,
        { userId: loginUserId },
        { headers: commonHeaders }
      )
      .then((r) => setStoreIssueNo(r.data.storeIssueNo || ""))
      .catch(() => toast.error("Failed to generate issue no"));
  };

  const loadStoreReqGrid = () => {
    axios
      .get(`${API}/storeIssue_addList`, {
        params: { branchId },
        headers: commonHeaders,
      })
      .then((r) => setStoreReqGrid(r.data.data || []))
      .catch(() => toast.error("Failed to load store requests"));
  };

  const loadProductions = (projId) => {
    axios
      .get(`${API}/storeIssue_productions`, {
        params: { projectId: projId },
        headers: commonHeaders,
      })
      .then((r) => setProductionList(r.data.productions || []))
      .catch(() => toast.error("Failed to load productions"));
  };

  const loadExistingDataFromSession = async () => {
    try {
      if (!existingStoreIssueNo || !existingIssueId) {
        toast.error("Edit session missing storeid or storeissueid");
        return;
      }

      const headerRes = await axios.get(`${API}/storeIssue_existingDetails`, {
        params: {
          storeIssueNo: existingStoreIssueNo,
          storeIssueId: existingIssueId,
        },
        headers: commonHeaders,
      });

      if (!headerRes.data.success) {
        toast.error(headerRes.data.message || "Failed to load issue header");
        return;
      }

      const h = headerRes.data.data;
      const resolvedStoreId = h.storeId || existingUserId || "";

      setStoreIssueNo(existingStoreIssueNo);
      setIssueDate(h.dateIssued ? h.dateIssued.split("T")[0] : todayStr());
      setCurrentStore(String(resolvedStoreId));
      setRemarks(h.remarks || "");
      setIssueType(h.issueType || "FS");
      setIssueFor(h.issueFor || "D");
      setVehicleType(h.vehicleType || "O");

      if (!resolvedStoreId) {
        toast.warning("Could not resolve storeId — detail items may not load");
        return;
      }

      const detailRes = await axios.get(`${API}/storeIssue_updateItems`, {
        params: {
          branchId,
          storeId: resolvedStoreId,
          storeIssueId: existingIssueId,
          isDepartment: h.issueFor === "D",
          isVehicle: h.issueFor === "V",
          isSparesReturn: h.issueFor === "S",
          isProject: h.issueFor === "P",
        },
        headers: commonHeaders,
      });

      if (!detailRes.data.success) {
        toast.error(detailRes.data.message || "Failed to load issue items");
        return;
      }

      const rows = detailRes.data.data || [];
      const mapped = rows.map(mapDbRowToGridRow);

      setGridItems(mapped);

      const codes = mapped.map((x) => x.fld_itemcode).filter(Boolean);
      setSelectedItems(codes);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load existing issue in edit mode");
    }
  };

  const handleSearchItems = async () => {
    if (!itemCategory) {
      toast.warning("Please select an Item Category");
      return;
    }

    if (!branchId) {
      toast.warning("Branch ID not found in localStorage");
      return;
    }

    if (!currentStore) {
      toast.warning("Please select a Store");
      return;
    }

    setSearching(true);

    try {
      const res = await axios.get(`${API}/storeIssue_searchItems`, {
        params: {
          itemCategory,
          itemCode,
          itemName,
          branchId,
          currentStore,
          issueDate,
        },
        headers: commonHeaders,
      });

      const normalized = normalizeSearchItems(res.data.items || []);
      setSearchResults(normalized);

      const gridCodes = gridItems.map((g) => g.fld_itemcode).filter(Boolean);
      setSelectedItems((prev) => Array.from(new Set([...prev, ...gridCodes])));

      if (normalized.length === 0) toast.info("No items found");
    } catch {
      toast.error("Item search failed");
    } finally {
      setSearching(false);
    }
  };

  const toggleSearchItem = (item) => {
    if (!item?.itemcode) return;
    setSelectedItems((prev) =>
      prev.includes(item.itemcode)
        ? prev.filter((c) => c !== item.itemcode)
        : [...prev, item.itemcode]
    );
  };

  const handleAddItems = async () => {
    if (selectedItems.length === 0) {
      toast.warning("Please select at least one item");
      return;
    }

    if (!currentStore) {
      toast.warning("Please select a Store");
      return;
    }

    try {
      let res;

      if (gridItems.length === 0) {
        res = await axios.post(
          `${API}/storeIssue_fillitemsgrid`,
          {
            branchid: branchId,
            storeid: parseInt(currentStore, 10),
            selectid: selectedItems.join(","),
            isDepartment: issueFor === "D",
          },
          {
            headers: commonHeaders,
          }
        );
      } else {
        const recoveritemcode = gridItems.map((g) => g.fld_itemcode).join(",");
        const recoverreqqtyadd = gridItems
          .map((g) => g.requestQty || "0")
          .join(",");
        const recoverissueqtyadd = gridItems
          .map((g) => g.issueQty || "0")
          .join(",");

        res = await axios.post(
          `${API}/storeIssue_fillitemsandvaluegrid`,
          {
            branchid: branchId,
            storeid: parseInt(currentStore, 10),
            selectid: selectedItems.join(","),
            recoveritemcode,
            recoverreqqtyadd,
            recoverissueqtyadd,
            isDepartment: issueFor === "D",
          },
          {
            headers: commonHeaders,
          }
        );
      }

      if (res.data.success) {
        const incoming = (res.data.items || []).map(mapDbRowToGridRow);

        const oldMap = new Map(gridItems.map((g) => [g.fld_itemcode, g]));
        const merged = incoming.map((row) => {
          const old = oldMap.get(row.fld_itemcode);
          if (!old) return row;
          return {
            ...row,
            requestQty: old.requestQty ?? row.requestQty,
            issueQty: old.issueQty ?? row.issueQty,
            departmentCode: old.departmentCode ?? row.departmentCode,
            departmentName: old.departmentName ?? row.departmentName,
            vehicleCode: old.vehicleCode ?? row.vehicleCode,
            vehicleName: old.vehicleName ?? row.vehicleName,
            detailId: old.detailId ?? row.detailId,
          };
        });

        setGridItems(merged);
        toast.success("Items added/updated in grid");
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to add items");
    }
  };

  const updateGridRow = (idx, field, value) => {
    setGridItems((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [field]: value };
      return copy;
    });
  };

  const removeGridRow = (idx) => {
    setGridItems((prev) => {
      const removed = prev[idx];
      const updated = prev.filter((_, i) => i !== idx);
      if (removed?.fld_itemcode) {
        setSelectedItems((sel) => sel.filter((c) => c !== removed.fld_itemcode));
      }
      return updated;
    });
  };

  const validate = () => {
    if (!issueDate) return toast.error("Please select Issue Date"), false;
    if (!currentStore) return toast.error("Please select a Store"), false;
    if (gridItems.length === 0) return toast.error("Please add at least one item"), false;

    for (let i = 0; i < gridItems.length; i++) {
      const row = gridItems[i];
      const qty = parseFloat(row.issueQty);

      if (!qty || qty <= 0) {
        toast.error(`Row ${i + 1}: Issue Qty must be greater than 0`);
        return false;
      }
      if (qty > parseFloat(row.availableQty || 0)) {
        toast.error(`Row ${i + 1}: Issue Qty exceeds Available Qty`);
        return false;
      }
      if (issueFor === "D" && !String(row.departmentCode || "").trim()) {
        toast.error(`Row ${i + 1}: Please enter Department`);
        return false;
      }
      if (issueFor === "V" && !String(row.vehicleCode || "").trim()) {
        toast.error(`Row ${i + 1}: Please enter Vehicle Code`);
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);

    try {
      const payload = {
        storeIssueId: isEditMode ? parseInt(existingIssueId, 10) : null,
        storeIssueNo,
        issueDate,
        remarks,
        loginUserId,
        branchId,
        storeId: parseInt(currentStore, 10),
        issueType,
        vehicleType,
        issueFor,
        approve: true,
        storeReqId: 0,
        vehicleHigher: vehicleType === "H",
        items: gridItems.map((row) => ({
          itemId: row.fld_itemid,
          issueQty: parseFloat(row.issueQty),
          requestQty: parseFloat(row.requestQty || 0),
          availableQty: parseFloat(row.availableQty || 0),
          departmentCode: row.departmentCode || "",
          departmentName: row.departmentName || "",
          vehicleCode: row.vehicleCode || "",
          vehicleName: row.vehicleName || "",
          supplierCode: row.supplierCode || "",
          supplierName: row.supplierName || "",
          tyreBatterySerialNo: row.tyreBatterySerialNo || "",
          tyreBatteryMake: row.tyreBatteryMake || "",
          srcSerialNo: row.srcSerialNo || "",
          hsdfillinghrkm: row.hsdfillinghrkm || "0",
          hsdfillinghrkm1: row.hsdfillinghrkm1 || "0",
          detailId: row.detailId || null,
        })),
      };

      const res = await axios.post(`${API}/storeIssue_submit`, payload, {
        headers: commonHeaders,
      });

      if (res.data.success) {
        toast.success(res.data.message || "Saved successfully");

        sessionStorage.removeItem("storeid");
        sessionStorage.removeItem("storeuserid");
        sessionStorage.removeItem("storeissueid");

        setTimeout(() => navigate("/inventory/StoreRequisition_Listing"), 1200);
      } else {
        toast.error(res.data.message || "Save failed");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Error saving store issue");
    } finally {
      setSaving(false);
    }
  };

  const card = `rounded-xl border p-5 ${
    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
  }`;
  const inputCls = `w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-${baseColor}-400 ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;
  const labelCls = `block text-sm font-medium mb-1 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;
  const thCls = `px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-white`;
  const tdCls = `px-3 py-2 text-sm ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;
  const miniInput = `px-2 py-1 border rounded text-xs w-full focus:outline-none ${
    isDark
      ? "bg-gray-700 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-800"
  }`;

  return (
    <LayoutWrapper>
      <HeaderBar
        title={isEditMode ? "EDIT STORE ISSUE NOTE" : "ADD STORE ISSUE NOTE"}
      />
      <ToastContainer position="top-right" autoClose={3000} />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <h1
            className={`text-xl font-bold ${
              isDark ? "text-white" : "text-gray-900"
            }`}
          >
            {isEditMode ? `Edit: ${storeIssueNo}` : "New Store Issue Note"}
          </h1>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/inventory/StoreRequisition_Listing")}
              className={`flex items-center gap-2 border px-4 py-2 rounded-lg text-sm transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700"
                  : "border-gray-300 text-gray-700 hover:bg-gray-100"
              }`}
            >
              <FaArrowLeft /> Back
            </button>

            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center gap-2 ${btn} disabled:opacity-50`}
            >
              {saving ? (
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
              ) : (
                <FaSave />
              )}
              {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        </div>

        <div className={`${card} mb-6`}>
          <h2
            className={`text-base font-semibold mb-4 ${
              isDark ? "text-white" : "text-gray-800"
            }`}
          >
            Issue Details
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <div>
              <label className={labelCls}>Store Issue Type</label>
              <div className="flex gap-5 mt-1">
                {[
                  { val: "FS", label: "From Store" },
                  { val: "FR", label: "From Store Request" },
                ].map((o) => (
                  <label
                    key={o.val}
                    className="flex items-center gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="radio"
                      value={o.val}
                      checked={issueType === o.val}
                      disabled={isEditMode}
                      onChange={() => setIssueType(o.val)}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls}>Store Issue No</label>
              <input
                type="text"
                value={storeIssueNo}
                readOnly
                className={inputCls + " bg-gray-100 cursor-not-allowed"}
              />
            </div>

            <div>
              <label className={labelCls}>
                Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={issueDate}
                max={todayStr()}
                onChange={(e) => setIssueDate(e.target.value)}
                className={inputCls}
              />
            </div>

            <div>
              <label className={labelCls}>
                From Store <span className="text-red-400">*</span>
              </label>
              <select
                value={currentStore}
                onChange={(e) => setCurrentStore(e.target.value)}
                disabled={isEditMode}
                className={inputCls}
              >
                {storeList.map((s, i) => (
                  <option key={i} value={s.fld_storeid}>
                    {s.fld_storename}
                  </option>
                ))}
              </select>
            </div>

            <div className="lg:col-span-2">
              <label className={labelCls}>Remarks</label>
              <input
                type="text"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Optional remarks"
                className={inputCls}
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm cursor-not-allowed opacity-60">
              <input type="checkbox" checked={approve} readOnly />
              Approve
            </label>
          </div>
        </div>

        {!isEditMode && (
          <div className={`${card} mb-6`}>
            <h2
              className={`text-base font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-800"
              }`}
            >
              Filtration Area
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-4">
              <div>
                <label className={labelCls}>Item Category</label>
                <select
                  value={itemCategory}
                  onChange={(e) => setItemCategory(e.target.value)}
                  className={inputCls}
                >
                  {itemCategories.map((c, i) => (
                    <option key={i} value={c.fld_itemcategoryid}>
                      {c.fld_itemcategory_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelCls}>Item Name</label>
                <input
                  type="text"
                  value={itemName}
                  onChange={(e) => setItemName(e.target.value)}
                  placeholder="Enter name"
                  className={inputCls}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchItems()}
                />
              </div>

              <div>
                <label className={labelCls}>Item Code</label>
                <input
                  type="text"
                  value={itemCode}
                  onChange={(e) => setItemCode(e.target.value)}
                  placeholder="Enter code"
                  className={inputCls}
                  onKeyDown={(e) => e.key === "Enter" && handleSearchItems()}
                />
              </div>
            </div>

            <div className="flex gap-3 mb-4">
              <button
                onClick={handleSearchItems}
                disabled={searching}
                className={`flex items-center gap-2 ${btn} disabled:opacity-50`}
              >
                {searching ? (
                  <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
                ) : (
                  <FaSearch />
                )}
                Search
              </button>

              {selectedItems.length > 0 && (
                <button
                  onClick={handleAddItems}
                  className={`flex items-center gap-2 ${btn}`}
                >
                  <FaPlus /> Add ({selectedItems.length}) to Grid
                </button>
              )}
            </div>

            {searchResults.length > 0 && (
              <div
                className={`rounded-lg border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                <div className="overflow-x-auto max-h-56 overflow-y-auto">
                  <table className="min-w-full text-sm">
                    <thead className={`bg-${baseColor}-${baseShade} sticky top-0`}>
                      <tr>
                        <th className={thCls + " w-10"} />
                        <th className={thCls}>S.No</th>
                        <th className={thCls}>Item Code / Name</th>
                      </tr>
                    </thead>

                    <tbody className={isDark ? "bg-gray-800" : "bg-white"}>
                      {searchResults.map((item, idx) => (
                        <tr
                          key={item.itemcode}
                          onClick={() => toggleSearchItem(item)}
                          className={`cursor-pointer transition-colors ${
                            selectedItems.includes(item.itemcode)
                              ? isDark
                                ? "bg-blue-900"
                                : "bg-blue-50"
                              : isDark
                              ? "hover:bg-gray-700"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-3 py-2 text-center">
                            <input
                              type="checkbox"
                              readOnly
                              checked={selectedItems.includes(item.itemcode)}
                            />
                          </td>
                          <td className={tdCls}>{idx + 1}</td>
                          <td className={tdCls}>{item.display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {gridItems.length > 0 && (
          <div className={`${card}`}>
            <h2
              className={`text-base font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-800"
              }`}
            >
              Items to Issue
            </h2>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    <th className={thCls}>#</th>
                    <th className={thCls}>Item Code</th>
                    <th className={thCls}>Item Name</th>
                    <th className={thCls}>Available Qty</th>
                    <th className={thCls}>Req Qty</th>
                                        <th className={thCls}>
                      Issue Qty <span className="text-red-300">*</span>
                    </th>
                    {issueFor === "D" && (
                      <th className={thCls}>
                        Department Code <span className="text-red-300">*</span>
                      </th>
                    )}
                    {issueFor === "V" && (
                      <th className={thCls}>
                        Vehicle Code <span className="text-red-300">*</span>
                      </th>
                    )}
                    {!isEditMode && <th className={thCls}>Remove</th>}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-700 bg-gray-800"
                      : "divide-gray-100 bg-white"
                  }`}
                >
                  {gridItems.map((row, idx) => (
                    <tr key={row.fld_itemcode + "-" + idx}>
                      <td className={tdCls}>{idx + 1}</td>
                      <td className={tdCls + " font-mono"}>{row.fld_itemcode}</td>
                      <td className={tdCls}>{row.fld_itemname}</td>
                      <td className={tdCls + " text-right"}>{row.availableQty}</td>

                      <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          value={row.requestQty}
                          onChange={(e) =>
                            updateGridRow(idx, "requestQty", e.target.value)
                          }
                          className={miniInput + " w-20"}
                        />
                      </td>

                                           <td className="px-3 py-2">
                        <input
                          type="number"
                          min="0"
                          value={row.issueQty}
                          onChange={(e) =>
                            updateGridRow(idx, "issueQty", e.target.value)
                          }
                          className={miniInput + " w-20"}
                        />
                      </td>

                      {issueFor === "D" && (
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.departmentCode}
                            onChange={(e) =>
                              updateGridRow(idx, "departmentCode", e.target.value)
                            }
                            placeholder="Dept Code"
                            className={miniInput + " w-24"}
                          />
                        </td>
                      )}

                      {issueFor === "V" && (
                        <td className="px-3 py-2">
                          <input
                            type="text"
                            value={row.vehicleCode}
                            onChange={(e) =>
                              updateGridRow(idx, "vehicleCode", e.target.value)
                            }
                            placeholder="Vehicle Code"
                            className={miniInput + " w-24"}
                          />
                        </td>
                      )}

                      {!isEditMode && (
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => removeGridRow(idx)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <FaTrash />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div
              className={`mt-3 pt-3 border-t text-right text-sm font-semibold ${
                isDark
                  ? "border-gray-700 text-gray-200"
                  : "border-gray-200 text-gray-800"
              }`}
            >
              Total Items: {gridItems.length} &nbsp;|&nbsp; Total Issue Qty:{" "}
              {gridItems
                .reduce((s, r) => s + (parseFloat(r.issueQty) || 0), 0)
                .toFixed(2)}
            </div>
          </div>
        )}

        {gridItems.length > 0 && (
          <div className="flex justify-end mt-6">
            <button
              onClick={handleSubmit}
              disabled={saving}
              className={`flex items-center gap-2 ${btn} px-8 disabled:opacity-50`}
            >
              {saving ? (
                <span className="animate-spin border-2 border-white border-t-transparent rounded-full w-4 h-4" />
              ) : (
                <FaSave />
              )}
              {saving ? "Saving..." : isEditMode ? "Update" : "Save"}
            </button>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default StoreIssueNoteAdd;